import { useState, useEffect, useCallback, useRef } from "react";

export function useNotifications(authToken) {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount]     = useState(0);
    const audioRef      = useRef(null);
    const audioUnlocked = useRef(false);

    // ✅ Create single Audio instance
    useEffect(() => {
        audioRef.current = new Audio("/notification.mp3");
        audioRef.current.volume = 1.0;
    }, []);

    // ✅ Unlock the SAME audio instance on first click
    useEffect(() => {
        const unlock = () => {
            if (audioUnlocked.current) return;
            audioRef.current.play()
                .then(() => {
                    audioRef.current.pause();
                    audioRef.current.currentTime = 0;
                    audioUnlocked.current = true;
                    console.log("🔊 Audio unlocked");
                })
                .catch(() => {});
            document.removeEventListener("click", unlock);
        };
        document.addEventListener("click", unlock);
        return () => document.removeEventListener("click", unlock);
    }, []);

    // ✅ Play the SAME audio instance
    const playNotificationSound = useCallback(() => {
        if (!audioUnlocked.current) {
            console.warn("🔇 Audio not yet unlocked — waiting for first click");
            return;
        }
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch((e) => console.warn("🔇 Audio error:", e));
    }, []);

    // Load history on mount
    useEffect(() => {
        if (!authToken) return;
        fetch(process.env.REACT_APP_BACKEND_API_URL+"/notification/getusernotification?user_id="+sessionStorage['id'], {
            headers: { Authorization: `Token ${authToken}` },
        })
        .then((r) => r.json())
        .then((data) => {
            setNotifications(Array.isArray(data) ? data : []);
            setUnreadCount(data.filter((n) => !n.is_read).length);
        })
        .catch((err) => console.error("❌ Fetch failed:", err));
    }, [authToken]);

    // WebSocket for live pushes
    useEffect(() => {
        // ✅ Read from sessionStorage — not authToken prop
        const token = sessionStorage.getItem("token");

        if (!token || token === "undefined" || token === "null") {
            console.error("❌ Invalid token in sessionStorage:", token);
            return;
        }

        // const backendUrl = process.env.REACT_APP_BACKEND_WS_API_URL;
        // const protocol = backendUrl.includes("localhost") || backendUrl.includes("127.0.0.1")
        //     ? "ws" : "wss";
        // const wsUrl = `${protocol}://${backendUrl}/ws/notifications/?token=${token}`;

        const protocol = window.location.protocol === "https:" ? "wss" : "ws";

        const wsHost =
            process.env.NODE_ENV === "development"
                ? process.env.REACT_APP_BACKEND_WS_API_URL
                : window.location.host;

        const wsUrl =
            `${protocol}://${wsHost}/ws/notifications/?token=${token}`;

        console.log("🔌 Connecting to:", wsUrl);

        let ws = null;
        const timer = setTimeout(() => {
            ws = new WebSocket(wsUrl);

            ws.onopen    = () => console.log("✅ WebSocket connected");
            ws.onerror   = (e) => console.error("❌ WebSocket error:", e);
            ws.onclose   = (e) => console.warn("🔌 WS closed:", e.code, e.reason);
            ws.onmessage = (e) => {
                console.log("📨 Notification received:", e.data);
                const notif = JSON.parse(e.data);
                setNotifications((prev) => [notif, ...prev]);
                setUnreadCount((prev) => prev + 1);
                playNotificationSound();
            };
        }, 100);

        return () => {
            clearTimeout(timer);
            if (ws) ws.close();
        };
    }, [playNotificationSound]);

    const markRead = useCallback(async (id) => {
        await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/notification/mark_as_read/?id=${id}&user_id=${sessionStorage['id']}`, { method: "PUT" });
        setNotifications((prev) =>
            prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
    }, []);

    const markAllRead = useCallback(async () => {
        await fetch(process.env.REACT_APP_BACKEND_API_URL+"/notification/mark_all_read/?&user_id="+sessionStorage['id'], { method: "PUT" });
        setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
        setUnreadCount(0);
    }, []);

    return { notifications, unreadCount, markRead, markAllRead };
}